import { PERMISSIONS } from '@repo/contracts';
import { lazy } from 'react';
import type { RouteObject } from 'react-router';

import { AppShell } from '@/app/app-shell';
import { APP_ROUTES } from '@/constants/app-routes';

import { RequireAuth } from './require-auth';
import { RequirePermission } from './require-permission';

const LoginPage = lazy(() => import('@/pages/login/LoginPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found/NotFoundPage'));
const RouteErrorPage = lazy(() => import('@/pages/route-error/RouteErrorPage'));
const DashboardHomeRoute = lazy(() => import('@/pages/dashboard/DashboardHomeRoute'));
const ClientsPage = lazy(() => import('@/pages/clients/ClientsPage'));
const ClientCreatePage = lazy(() => import('@/pages/clients/ClientCreatePage'));
const ClientDetailPage = lazy(() => import('@/pages/clients/ClientDetailPage'));
const ClientEditPage = lazy(() => import('@/pages/clients/ClientEditPage'));
const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage'));
const ProjectCreatePage = lazy(() => import('@/pages/projects/ProjectCreatePage'));
const ProjectDetailPage = lazy(() => import('@/pages/projects/ProjectDetailPage'));
const ProjectEditPage = lazy(() => import('@/pages/projects/ProjectEditPage'));
const TaskBoardPage = lazy(() => import('@/pages/board/TaskBoardPage'));
const AllocationsPage = lazy(() => import('@/pages/allocations/AllocationsPage'));
const AllocationCreatePage = lazy(() => import('@/pages/allocations/AllocationCreatePage'));
const ResourcePage = lazy(() => import('@/pages/resources/ResourcePage'));
const ApprovalsPage = lazy(() => import('@/pages/approvals/ApprovalsPage'));
const UtilisationPage = lazy(() => import('@/pages/utilisation/UtilisationPage'));
const DepartmentDetailPage = lazy(() => import('@/pages/utilisation/DepartmentDetailPage'));
const ResourceRolesPage = lazy(() => import('@/pages/admin/resource-roles/ResourceRolesPage'));

export const routes: RouteObject[] = [
  { path: APP_ROUTES.login, element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    element: <RequireAuth />,
    // Last resort: only reached if AppShell itself fails to render, so it
    // renders RouteErrorPage without the sidebar chrome that just broke.
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            // A pathless layout route so the boundary sits inside AppShell:
            // an error here keeps the sidebar, instead of errorElement on
            // AppShell itself replacing the whole shell.
            errorElement: <RouteErrorPage />,
            children: [
              { path: APP_ROUTES.dashboard, element: <DashboardHomeRoute /> },
              {
                element: <RequirePermission permission={PERMISSIONS.clientRead} />,
                children: [
                  { path: APP_ROUTES.clients.list, element: <ClientsPage /> },
                  { path: APP_ROUTES.clients.create, element: <ClientCreatePage /> },
                  { path: APP_ROUTES.clients.detail(), element: <ClientDetailPage /> },
                  { path: APP_ROUTES.clients.edit(), element: <ClientEditPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.projectRead} />,
                children: [
                  { path: APP_ROUTES.projects.list, element: <ProjectsPage /> },
                  { path: APP_ROUTES.projects.create, element: <ProjectCreatePage /> },
                  { path: APP_ROUTES.projects.detail(), element: <ProjectDetailPage /> },
                  { path: APP_ROUTES.projects.edit(), element: <ProjectEditPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.taskRead} />,
                children: [{ path: APP_ROUTES.projects.board(), element: <TaskBoardPage /> }],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.allocationRead} />,
                children: [
                  { path: APP_ROUTES.allocations.list, element: <AllocationsPage /> },
                  { path: APP_ROUTES.allocations.create, element: <AllocationCreatePage /> },
                  { path: APP_ROUTES.resources.detail(), element: <ResourcePage /> },
                  { path: APP_ROUTES.utilisation.list, element: <UtilisationPage /> },
                  { path: APP_ROUTES.utilisation.department(), element: <DepartmentDetailPage /> },
                ],
              },
              { path: APP_ROUTES.approvals, element: <ApprovalsPage /> },
              {
                element: <RequirePermission permission={PERMISSIONS.resourceRoleRead} />,
                children: [
                  { path: APP_ROUTES.admin.resourceRoles, element: <ResourceRolesPage /> },
                ],
              },
            ],
          },
          // Last child, so it never shadows a real route above it. Renders
          // inside AppShell, keeping the sidebar for an unmatched URL.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
